/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Governed cut-overs, rollback and the readiness report (src/doc/swift/cutover-and-rollback.md,
 * src/doc/swift/bandwidth-and-readiness.md). Every call is quiet: the dialogs and pages show the
 * API's own message (403 four-eyes, 409 wrong state...) next to the button that failed, rather
 * than a generic toast.
 */

import { INSTANCE_OSMORPHING_MINION_POOL_MAPPINGS } from "@src/components/modules/WizardModule/WizardOptions";
import DefaultOptionsSchemaPlugin from "@src/plugins/default/OptionsSchemaPlugin";
import Api from "@src/utils/ApiCaller";
import configLoader from "@src/utils/Config";

import type {
  CutoverAction,
  CutoverDeploymentOptions,
  CutoverListFilters,
  CutoverRequest,
  ReadinessReport,
  ReadinessRequest,
  RollbackTargetAction,
} from "@src/@types/Cutover";
import type { Field } from "@src/@types/Field";
import type { InstanceScript } from "@src/@types/Instance";
import type { DeploymentItemDetails, UserScriptData } from "@src/@types/MainItem";

/** The message of a failed API call: Swift's `{"error": {"message"}}`, webob's `{"<fault>": {"message"}}`, or the status. */
export const apiErrorMessage = (err: any, fallback = "The request failed."): string => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  const data = err.data ?? err.response?.data;
  if (data && typeof data === "object") {
    if (data.error?.message) return String(data.error.message);
    if (data.description) return String(data.description);
    if (data.message) return String(data.message);
    const inner = Object.values(data).find((v: any) => v && typeof v === "object" && v.message) as any;
    if (inner) return String(inner.message);
  }
  if (typeof data === "string" && data.trim() && !/^\s*</.test(data)) return data.trim().slice(0, 500);
  if (err.status) return `${fallback} (HTTP ${err.status}${err.statusText ? ` ${err.statusText}` : ""})`;
  if (err.message) return String(err.message);
  return fallback;
};

export const apiErrorStatus = (err: any): number | null =>
  (err && (err.status || err.response?.status)) || null;

/** The body the existing deployment form produces, as a cut-over request's `deployment_options`. */
export const buildDeploymentOptions = (opts: {
  fields: Field[];
  uploadedUserScripts: InstanceScript[];
  removedUserScripts: InstanceScript[];
  userScriptData: UserScriptData | null | undefined;
  minionPoolMappings: { [instance: string]: string | null };
  rollbackRetentionHours?: number | null;
}): CutoverDeploymentOptions => {
  const options: any = {};
  opts.fields.forEach(f => {
    if (["clone_disks", "force", "skip_os_morphing"].includes(f.name)) {
      options[f.name] = Boolean(f.value ?? f.default ?? false);
    }
  });
  if (opts.uploadedUserScripts.length || opts.removedUserScripts.length) {
    options.user_scripts = new DefaultOptionsSchemaPlugin().getUserScripts(
      opts.uploadedUserScripts,
      opts.removedUserScripts,
      opts.userScriptData,
    );
  }
  const mappings: { [k: string]: string } = {};
  Object.keys(opts.minionPoolMappings || {}).forEach(k => {
    const v = opts.minionPoolMappings[k];
    if (v) mappings[k] = v;
  });
  if (Object.keys(mappings).length) {
    options[INSTANCE_OSMORPHING_MINION_POOL_MAPPINGS] = mappings;
  }
  if (opts.rollbackRetentionHours !== undefined && opts.rollbackRetentionHours !== null) {
    options.rollback_retention_hours = opts.rollbackRetentionHours;
  }
  return options;
};

const base = () => `${configLoader.config.servicesUrls.coriolis}/${Api.projectId}`;

const query = (filters: CutoverListFilters = {}) => {
  const params: string[] = [];
  const add = (k: string, v: any) => {
    if (v !== undefined && v !== null && v !== "") params.push(`${k}=${encodeURIComponent(String(v))}`);
  };
  add("transfer_id", filters.transfer_id);
  add("deployment_id", filters.deployment_id);
  add("status", Array.isArray(filters.status) ? filters.status.join(",") : filters.status);
  add("since", filters.since);
  add("until", filters.until);
  add("limit", filters.limit);
  return params.length ? `?${params.join("&")}` : "";
};

class CutoverSource {
  async list(filters?: CutoverListFilters, opts?: { skipLog?: boolean }): Promise<CutoverRequest[]> {
    const response = await Api.send({
      url: `${base()}/cutover_requests${query(filters)}`,
      quietError: true,
      skipLog: opts?.skipLog,
    });
    return response.data.cutover_requests || [];
  }

  /** A transfer's requests, history included. */
  async listForTransfer(transferId: string, filters?: CutoverListFilters): Promise<CutoverRequest[]> {
    const response = await Api.send({
      url: `${base()}/transfers/${encodeURIComponent(transferId)}/cutover_requests${query(filters)}`,
      quietError: true,
      skipLog: true,
    });
    return response.data.cutover_requests || [];
  }

  async get(id: string, opts?: { skipLog?: boolean }): Promise<CutoverRequest> {
    const response = await Api.send({
      url: `${base()}/cutover_requests/${encodeURIComponent(id)}`,
      quietError: true,
      skipLog: opts?.skipLog,
    });
    return response.data.cutover_request;
  }

  async create(body: {
    transfer_id: string;
    window_start: string;
    window_end: string;
    reason?: string;
    instances?: string[] | null;
    deployment_options?: CutoverDeploymentOptions;
  }): Promise<CutoverRequest> {
    const payload: any = { ...body };
    if (!payload.instances) delete payload.instances;
    if (!payload.reason) delete payload.reason;
    const response = await Api.send({
      url: `${base()}/cutover_requests`,
      method: "POST",
      data: { cutover_request: payload },
      quietError: true,
    });
    return response.data.cutover_request;
  }

  async action(id: string, action: CutoverAction, comment?: string): Promise<CutoverRequest> {
    const inner: any = {};
    if (comment && comment.trim()) inner.comment = comment.trim();
    const response = await Api.send({
      url: `${base()}/cutover_requests/${encodeURIComponent(id)}/actions`,
      method: "POST",
      data: { [action]: inner },
      quietError: true,
    });
    return response.data.cutover_request;
  }

  async rollback(
    deploymentId: string,
    opts: { powerOnSource: boolean; targetAction: RollbackTargetAction },
  ): Promise<DeploymentItemDetails> {
    const response = await Api.send({
      url: `${base()}/deployments/${encodeURIComponent(deploymentId)}/actions`,
      method: "POST",
      data: { rollback: { power_on_source: opts.powerOnSource, target_action: opts.targetAction } },
      quietError: true,
    });
    return response.data?.deployment;
  }

  /** POST /endpoints/{source}/actions {"readiness": {...}}; read-only, answers within ~timeout_seconds. */
  async readiness(sourceEndpointId: string, body: ReadinessRequest): Promise<ReadinessReport> {
    const timeout = ((body.timeout_seconds || 45) + 30) * 1000;
    const response = await Api.send({
      url: `${base()}/endpoints/${encodeURIComponent(sourceEndpointId)}/actions`,
      method: "POST",
      data: { readiness: body },
      quietError: true,
      timeout,
    });
    return response.data.readiness;
  }
}

export default new CutoverSource();
